/**
 * A chemical formula set properly — H₂SO₄, not "H2SO4". Rendered through
 * KaTeX's mhchem extension, which is built for exactly this: `\ce{H2SO4(aq)}`
 * handles subscripts, state symbols and bracketed groups without any of them
 * being spelled out by hand per substance.
 *
 * KaTeX is called directly rather than through react-katex: that wrapper
 * resolves its own copy of React in this workspace, and a second React means
 * every hook in the app throws ("resolveDispatcher().useRef of null"). The
 * output is static markup, so a wrapper buys nothing anyway.
 *
 * A substance with no formula (an indicator, say) is named in words instead,
 * so callers can pass either and get something readable.
 */
import { useMemo } from 'react';
import katex from 'katex';
import 'katex/dist/katex.min.css';
// Registers \ce with KaTeX. Imported for effect, once.
import 'katex/contrib/mhchem';

export function ChemFormula({ formula, fallback }: { formula: string; fallback: string }): React.JSX.Element {
  const html = useMemo(() => {
    if (!formula) return null;
    try {
      return katex.renderToString(`\\ce{${formula}}`, { throwOnError: false, displayMode: false });
    } catch {
      // A formula KaTeX can't parse is still a formula worth showing — fall
      // back to the raw text rather than losing the label entirely.
      return null;
    }
  }, [formula]);

  if (!html) return <>{formula || fallback}</>;
  return <span dangerouslySetInnerHTML={{ __html: html }} />;
}
